/**
 * Common App Redux Slice
 *
 * This slice manages global application state shared across the entire application:
 * - User location (detected via geolocation/IP, or manually entered)
 * - Current authenticated user
 * - Browser viewport dimensions
 *
 * State Structure:
 * - detectedLocation: Auto-detected user location from IP/geolocation API
 * - enteredLocation: User-manually-entered location (zip code, address)
 * - user: Current authenticated user data
 * - viewport: Current browser window dimensions
 *
 * @migration Migrated to Redux Toolkit createSlice pattern (2024)
 */
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { ILocation, ILocationGuessResponse } from "../../models/location";
import { IViewport } from "../../models/screen";
import { IUser } from "../../models/user";
import { isZipLocation } from "../../utils/guards";
import { getChatConnector } from "../chat/index";
import { IReduxState } from "./reducers.interfaces";
import { loadCurrentUser } from "./thunks";

const getInitialViewport = (): { width: number; height: number } => {
    const window = getWindow();
    return {
        width: window ? window.innerWidth : 1440,
        height: window ? window.innerHeight : 700,
    };
};

const defaults: IReduxState = {
    detectedLocation: null,
    enteredLocation: null,
    user: null,
    viewport: getInitialViewport(),
};

const commonSlice = createSlice({
    name: "common",
    initialState: defaults,
    reducers: {
        updateDetectedLocation: (
            state,
            action: PayloadAction<ILocationGuessResponse>,
        ) => {
            state.detectedLocation = action.payload;
        },
        updateEnteredLocation: (state, action: PayloadAction<ILocation>) => {
            if (isZipLocation(action.payload)) {
                // Slice zip code to 5 characters and create new location object
                state.enteredLocation = {
                    ...action.payload,
                    zip: action.payload.zip.slice(0, 5),
                };
            } else {
                state.enteredLocation = action.payload;
            }
        },
        setUser: (state, action: PayloadAction<IUser>) => {
            if (action.payload.email) {
                getChatConnector().setUserData(action.payload);
            }
            state.user = action.payload;
        },
        setViewport: (state, action: PayloadAction<IViewport>) => {
            state.viewport = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(loadCurrentUser.fulfilled, (state, action) => {
            if (action.payload.email) {
                getChatConnector().setUserData(action.payload);
            }
            state.user = action.payload;
        });
    },
});

export const {
    updateDetectedLocation,
    updateEnteredLocation,
    setUser,
    setViewport,
} = commonSlice.actions;
export const reducers = commonSlice.reducer;
